/**
 * Frontend Security Protection
 *
 * Protects against:
 * - Code extraction from console
 * - DevTools access
 * - Right-click context menu
 * - Text selection
 * - Screenshot/recording
 * - Source code viewing
 *
 * Copyright (c) 2025 Tools & Bubbles Professional Cleaning Services
 * All rights reserved. Unauthorized copying, modification, or distribution is prohibited.
 */

import { getErrorSurfaceBranding } from './errorSurfaceBranding';

// Copyright notice
const COPYRIGHT_NOTICE = `
╔════════════════════════════════════════════════════════════════╗
║  Copyright (c) 2025 Tools & Bubbles Professional Cleaning     ║
║  Services. All rights reserved.                                ║
║                                                                ║
║  This software and associated documentation files (the         ║
║  "Software") are proprietary and confidential.                 ║
║                                                                ║
║  Unauthorized copying, modification, distribution, or use     ║
║  of this Software, via any medium, is strictly prohibited.   ║
║                                                                ║
║  Violators will be prosecuted to the maximum extent            ║
║  permitted by law.                                             ║
╚════════════════════════════════════════════════════════════════╝
`;

/**
 * Disable console access
 */
export function disableConsole(): void {
  if (typeof window === 'undefined') return;

  // Override console methods
  const noop = () => {};
  const originalConsole = { ...console };

  Object.keys(console).forEach((key) => {
    try {
      (console as any)[key] = noop;
    } catch (e) {
      // Ignore errors
    }
  });

  // Prevent console restoration
  Object.defineProperty(window, 'console', {
    value: new Proxy(console, {
      get: () => noop,
      set: () => false,
    }),
    writable: false,
    configurable: false,
  });

  // Log copyright notice if console is accessed
  setTimeout(() => {
    try {
      console.log(COPYRIGHT_NOTICE);
      console.warn('Unauthorized access detected. This action has been logged.');
    } catch (e) {
      // Ignore
    }
  }, 100);
}

/**
 * Disable developer tools
 */
export function disableDevTools(): void {
  if (typeof window === 'undefined') return;

  // Disable F12
  document.addEventListener('keydown', (e) => {
    // F12
    if (e.key === 'F12') {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }

    // Ctrl+Shift+I (DevTools)
    if (e.ctrlKey && e.shiftKey && e.key === 'I') {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }

    // Ctrl+Shift+J (Console)
    if (e.ctrlKey && e.shiftKey && e.key === 'J') {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }

    // Ctrl+Shift+C (Inspect Element)
    if (e.ctrlKey && e.shiftKey && e.key === 'C') {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }

    // Ctrl+U (View Source)
    if (e.ctrlKey && e.key === 'u') {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }

    // Ctrl+S (Save Page)
    if (e.ctrlKey && e.key === 's') {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  });

  // Detect DevTools opening
  let devToolsOpen = false;
  const element = new Image();
  Object.defineProperty(element, 'id', {
    get: () => {
      devToolsOpen = true;
      return 'devtools-detector';
    },
  });

  setInterval(() => {
    devToolsOpen = false;
    console.log(element);
    if (devToolsOpen) {
      // DevTools detected
      document.body.innerHTML = '';
      document.write(`
        <html>
          <head>
            <title>Access Denied</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                background: #1a1a1a;
                color: #fff;
                text-align: center;
              }
              .message {
                padding: 40px;
                border: 2px solid #ff0000;
                border-radius: 10px;
                background: #2a2a2a;
              }
            </style>
          </head>
          <body>
            <div class="message">
              <h1>⚠️ Unauthorized Access Detected</h1>
              <p>Developer tools are not allowed on this application.</p>
              <p>Please close the developer tools and refresh the page.</p>
            </div>
          </body>
        </html>
      `);
    }
  }, 1000);
}

/**
 * Disable right-click context menu
 */
export function disableRightClick(): void {
  if (typeof document === 'undefined') return;

  document.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  });

  // Also disable on touch devices
  document.addEventListener('touchstart', (e) => {
    if (e.touches.length > 1) {
      e.preventDefault();
    }
  });
}

/** True when the event target is (or is inside) a field where users must select or type text. */
function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const el = target.closest('input, textarea, select, [contenteditable="true"]');
  return el !== null;
}

/**
 * Disable text selection
 */
export function disableTextSelection(): void {
  if (typeof document === 'undefined') return;

  document.addEventListener('selectstart', (e) => {
    if (isTextEntryTarget(e.target)) return;
    e.preventDefault();
    return false;
  });

  document.addEventListener('dragstart', (e) => {
    if (isTextEntryTarget(e.target)) return;
    e.preventDefault();
    return false;
  });

  // CSS-based protection
  const style = document.createElement('style');
  style.textContent = `
    * {
      -webkit-user-select: none !important;
      -moz-user-select: none !important;
      -ms-user-select: none !important;
      user-select: none !important;
      -webkit-touch-callout: none !important;
      -webkit-tap-highlight-color: transparent !important;
    }
    
    input, textarea, select {
      -webkit-user-select: text !important;
      -moz-user-select: text !important;
      -ms-user-select: text !important;
      user-select: text !important;
    }
  `;
  document.head.appendChild(style);
}

/**
 * Disable keyboard shortcuts for saving/viewing source
 */
export function disableKeyboardShortcuts(): void {
  if (typeof document === 'undefined') return;

  document.addEventListener('keydown', (e) => {
    if (isTextEntryTarget(e.target)) {
      // Allow normal editing shortcuts inside form fields only
      return;
    }

    // Disable Ctrl+A (Select All)
    if (e.ctrlKey && e.key === 'a') {
      e.preventDefault();
      return false;
    }

    // Disable Ctrl+C (Copy)
    if (e.ctrlKey && e.key === 'c') {
      e.preventDefault();
      return false;
    }

    // Disable Ctrl+V (Paste)
    if (e.ctrlKey && e.key === 'v') {
      e.preventDefault();
      return false;
    }

    // Disable Ctrl+X (Cut)
    if (e.ctrlKey && e.key === 'x') {
      e.preventDefault();
      return false;
    }

    // Disable Ctrl+P (Print)
    if (e.ctrlKey && e.key === 'p') {
      e.preventDefault();
      return false;
    }

    // Disable Ctrl+S (Save)
    if (e.ctrlKey && e.key === 's') {
      e.preventDefault();
      return false;
    }
  });
}

/**
 * Add watermark to page
 */
export function addWatermark(): void {
  if (typeof document === 'undefined') return;

  const watermark = document.createElement('div');
  watermark.id = 'security-watermark';
  watermark.textContent = '© 2026 Tools & Bubbles - Confidential';
  watermark.style.cssText = `
    position: fixed;
    bottom: 10px;
    right: 10px;
    font-size: 10px;
    color: rgba(0, 0, 0, 0.1);
    pointer-events: none;
    z-index: 9999;
    user-select: none;
    font-family: Arial, sans-serif;
  `;
  document.body.appendChild(watermark);
}

/**
 * Prevent screenshot/recording (limited effectiveness)
 */
export function preventScreenshot(): void {
  if (typeof document === 'undefined') return;

  // Disable print screen (limited browser support)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'PrintScreen') {
      e.preventDefault();
      navigator.clipboard.writeText('');
      return false;
    }
  });

  // Add CSS to make screenshots less useful
  const style = document.createElement('style');
  style.textContent = `
    @media print {
      * {
        display: none !important;
      }
      body::before {
        content: "Copyright (c) 2026 Tools & Bubbles. Unauthorized reproduction prohibited.";
        display: block !important;
      }
    }
  `;
  document.head.appendChild(style);
}

/**
 * Obfuscate error messages
 */
export function obfuscateErrors(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (e) => {
    e.preventDefault();
    // Log to server instead of console
    // You can implement server-side logging here
    return false;
  });

  window.addEventListener('unhandledrejection', (e) => {
    e.preventDefault();
    return false;
  });
}

/**
 * Add copyright header to page
 */
export function addCopyrightHeader(): void {
  if (typeof document === 'undefined') return;

  const companyName = getErrorSurfaceBranding().productName;
  const meta = document.createElement('meta');
  meta.name = 'copyright';
  meta.content = `Copyright (c) 2026 ${companyName}. All rights reserved.`;
  document.head.appendChild(meta);

  const originalTitle = document.title;
  document.title = `${originalTitle} - © 2026 ${companyName}`;
}

/**
 * Check if current user is admin or super_admin
 */
async function isAdminUser(): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  try {
    // Import supabase client
    const { supabase } = await import('../lib/supabase');

    // Check if user is logged in
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) return false;

    // Get user profile to check role
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .maybeSingle();

    if (profileError || !profile) return false;

    // Allow devtools for admin and super_admin roles
    return profile.role === 'admin' || profile.role === 'super_admin';
  } catch (error) {
    // If check fails, default to secure (disable devtools)
    console.warn('Could not verify admin status, defaulting to secure mode:', error);
    return false;
  }
}

/**
 * Initialize all security protections
 */
export async function initializeSecurityProtection(): Promise<void> {
  if (typeof window === 'undefined') return;

  // Only enable in production
  if (import.meta.env.MODE === 'production' || import.meta.env.PROD) {
    // Check if user is admin before applying restrictions
    const isAdmin = await isAdminUser();

    if (!isAdmin) {
      // Apply all security restrictions for non-admin users
      disableConsole();
      disableDevTools();
      disableRightClick();
      disableTextSelection();
      disableKeyboardShortcuts();
      preventScreenshot();
      obfuscateErrors();
      addWatermark();
      addCopyrightHeader();

      // Log security initialization (to server in production)
      console.log('Security protection initialized');
    } else {
      // Admin users: Only apply minimal protections (watermark and copyright)
      addWatermark();
      addCopyrightHeader();
      console.log('Security protection initialized (admin mode - devtools enabled)');
    }
  }
}

/**
 * Export copyright notice for use in components
 */
export const COPYRIGHT = {
  notice: COPYRIGHT_NOTICE,
  year: 2025,
  company: 'Tools & Bubbles Professional Cleaning Services',
  message: 'All rights reserved. Unauthorized copying, modification, or distribution is prohibited.',
};
